import React, { useState } from "react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import CinematicShell from "@/components/cinematic/CinematicShell";
import { AuditRefineryStill } from "@/components/cinematic/stills";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, Upload } from "lucide-react";

const initialForm = {
  contact_name: "", email: "", org_name: "", role_title: "", phone: "",
  state: "", race_or_office: "", committee_type: "campaign",
  est_record_count: "", qualifies_america_first: true, file_note: "",
  delivery_method: "email",
};

export default function AuditPage() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form };
      if (payload.est_record_count === "") delete payload.est_record_count;
      else payload.est_record_count = parseInt(payload.est_record_count, 10);
      const { data } = await api.post("/intake/audit", payload);
      setAudit(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const upload = async () => {
    if (!file || !audit) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/intake/audit/${audit.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <PublicNav />
      <div className="relative dt6-cinematic-vignette" style={{ minHeight: "440px" }}>
        <CinematicShell
          sceneImport={() => import("@/components/cinematic/scenes/AuditRefinery")}
          stillSrc={AuditRefineryStill}
          testId="audit-hero-scene"
          className="dt6-cinematic-grain"
          style={{ position: "absolute", inset: 0, zIndex: 0 }}
        />
        <Section className="pt-16 pb-8 relative dt6-cinematic-copy" testId="audit-hero">
          <div className="dt6-chip dt6-chip-primary mb-4">/ FLAGSHIP FUNNEL</div>
          <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight max-w-4xl">
            Free voter file <span style={{ color: "var(--dt6-primary)" }}>audit.</span>
          </h1>
          <p className="mt-4 text-lg text-[color:var(--dt6-muted)] max-w-3xl">
            Upload up to 5,000 records. We return a health report: undeliverable, movers, deceased, duplicates,
            missing phone or email, TCPA exposure. Turnaround: 3 business days. Qualified America-First engagements only.
          </p>
        </Section>
      </div>

      <Section className="py-8" testId="audit-form-section">
        {!audit ? (
          <form onSubmit={submit} className="dt6-card p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="audit-form">
            <div>
              <label className="dt6-label">Contact name</label>
              <input className="dt6-input" required value={form.contact_name} onChange={(e)=>setForm({...form,contact_name:e.target.value})} data-testid="audit-contact-name" />
            </div>
            <div>
              <label className="dt6-label">Work email</label>
              <input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="audit-email" />
            </div>
            <div>
              <label className="dt6-label">Organization</label>
              <input className="dt6-input" required value={form.org_name} onChange={(e)=>setForm({...form,org_name:e.target.value})} data-testid="audit-org" />
            </div>
            <div>
              <label className="dt6-label">Your role</label>
              <input className="dt6-input" placeholder="e.g., Campaign Manager" value={form.role_title} onChange={(e)=>setForm({...form,role_title:e.target.value})} data-testid="audit-role" />
            </div>
            <div>
              <label className="dt6-label">Phone</label>
              <input className="dt6-input" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="audit-phone" />
            </div>
            <div>
              <label className="dt6-label">State</label>
              <input className="dt6-input" placeholder="e.g., PA" value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} data-testid="audit-state" />
            </div>
            <div>
              <label className="dt6-label">Race or office</label>
              <input className="dt6-input" value={form.race_or_office} onChange={(e)=>setForm({...form,race_or_office:e.target.value})} data-testid="audit-race" />
            </div>
            <div>
              <label className="dt6-label">Committee type</label>
              <select className="dt6-select" value={form.committee_type} onChange={(e)=>setForm({...form,committee_type:e.target.value})} data-testid="audit-committee">
                <option value="campaign">Campaign</option>
                <option value="pac">PAC (Independent-expenditure)</option>
                <option value="party">Party committee</option>
                <option value="movement">Movement org</option>
                <option value="consultant">Consultant / agency</option>
              </select>
            </div>
            <div>
              <label className="dt6-label">Est. record count</label>
              <input className="dt6-input" type="number" min="1" max="5000" placeholder="up to 5,000" value={form.est_record_count} onChange={(e)=>setForm({...form,est_record_count:e.target.value})} data-testid="audit-record-count" />
            </div>
            <div>
              <label className="dt6-label">Delivery</label>
              <select className="dt6-select" value={form.delivery_method} onChange={(e)=>setForm({...form,delivery_method:e.target.value})} data-testid="audit-delivery">
                <option value="email">Email report</option>
                <option value="portal">DT6 client portal</option>
              </select>
            </div>
            <div className="md:col-span-2 flex items-start gap-3">
              <input
                id="qualify"
                type="checkbox"
                checked={form.qualifies_america_first}
                onChange={(e)=>setForm({...form,qualifies_america_first:e.target.checked})}
                data-testid="audit-qualify"
                className="mt-1"
              />
              <label htmlFor="qualify" className="text-sm text-[color:var(--dt6-muted)]">
                We confirm this file is for an America-First engagement — campaign, PAC, committee, or movement — and we accept that DT6 may decline non-qualified requests.
              </label>
            </div>
            {error && <div className="md:col-span-2 text-sm text-[color:var(--dt6-danger)]" data-testid="audit-form-error">{error}</div>}
            <div className="md:col-span-2 flex items-center justify-between">
              <div className="text-xs text-[color:var(--dt6-muted)] font-mono tracking-[0.14em] uppercase">
                / STEP 1 OF 2 · SUBMIT REQUEST
              </div>
              <button type="submit" className="dt6-btn dt6-btn-primary" disabled={loading || !form.qualifies_america_first} data-testid="audit-submit">
                {loading ? "SUBMITTING..." : "Submit request"}
              </button>
            </div>
          </form>
        ) : (
          <div className="dt6-card p-8" data-testid="audit-uploaded-section">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 size={24} strokeWidth={1.5} className="text-[color:var(--dt6-success)]" />
              <div className="font-heading text-xl font-bold">Request received.</div>
            </div>
            <div className="font-mono text-xs text-[color:var(--dt6-muted)] tracking-[0.14em]" data-testid="audit-request-id">
              REQUEST ID / {audit.id.slice(0, 8).toUpperCase()}
            </div>
            <p className="mt-4 text-sm text-[color:var(--dt6-muted)]">
              Upload your CSV or XLSX now (up to 5,000 records) or send later. You'll receive a confirmation email.
            </p>

            {uploaded ? (
              <div className="dt6-empty mt-6" data-testid="audit-upload-success">
                FILE RECEIVED. TURNAROUND: 3 BUSINESS DAYS.
              </div>
            ) : (
              <div className="mt-6 dt6-card p-6" style={{ background: "var(--dt6-bg)" }}>
                <label className="dt6-label">File (CSV or XLSX)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="dt6-input"
                    data-testid="audit-file-input"
                  />
                  <button
                    type="button"
                    className="dt6-btn dt6-btn-primary"
                    onClick={upload}
                    disabled={!file || uploading}
                    data-testid="audit-file-upload"
                  >
                    <Upload size={14} /> {uploading ? "UPLOADING..." : "Upload"}
                  </button>
                </div>
                {error && <div className="mt-3 text-sm text-[color:var(--dt6-danger)]" data-testid="audit-upload-error">{error}</div>}
                <div className="mt-4 text-xs text-[color:var(--dt6-muted)]">
                  Prefer to send later? Reply to the confirmation email with your file.
                </div>
              </div>
            )}
          </div>
        )}
      </Section>

      <PublicFooter />
    </>
  );
}
